import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { DELIVERY_TABS } from './common';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';

export function DeliveryOps() {
  const { deliveries, assignRider, riderAvailability } = useApp();
  const [tab, setTab] = useState('new');
  const [assignTarget, setAssignTarget] = useState(null);
  const [staffRiders, setStaffRiders] = useState([]);
  useEffect(() => {
    api.get('/admin/users')
      .then((r) => setStaffRiders(r.data.filter((u) => u.role === 'rider').map((u) => ({ id: u.riderId || u.id, name: u.name, zone: 'Kelantan' }))))
      .catch(() => {});
  }, []);
  const riderRoster = staffRiders;
  const active = DELIVERY_TABS.find((t) => t.id === tab);
  const rows = deliveries.filter(active.match);
  return (
    <div data-testid="admin-delivery">
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-kelate-line">
        {DELIVERY_TABS.map((t) => {
          const count = deliveries.filter(t.match).length;
          return (
            <button key={t.id} data-testid={`delivery-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm ${tab === t.id ? 'border-b-2 border-kelate-darkwood font-semibold text-kelate-darkwood' : 'text-kelate-soft'}`}>
              {t.label}
              {count > 0 && <span className="rounded-full bg-secondary px-1.5 text-[10px] font-semibold text-kelate-darkwood">{count}</span>}
            </button>
          );
        })}
      </div>
      {rows.length === 0 && <EmptyState icon={Truck} title="Nothing here" description="Delivery jobs in this stage will appear here." />}
      <div className="space-y-3">
        {rows.map((d) => (
          <div key={d.id} data-testid={`delivery-job-${d.deliveryNumber}`} className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-kelate-ink">{d.deliveryNumber} <span className="text-xs font-normal text-kelate-soft">· Order #{d.orderNumber}</span></span>
              <StatusBadge status={d.deliveryStatus} />
            </div>
            <div className="mt-2 grid gap-1.5 text-xs text-kelate-soft sm:grid-cols-2">
              <span>Pickup: <b className="text-kelate-ink">{d.merchantName}</b> — {d.pickupAddress}</span>
              <span>Deliver to: {d.customerName} — {d.deliveryAddress} ({d.customerPhone})</span>
              <span>Products: {d.products}</span>
              <span>Amount due: <b className="text-kelate-darkwood">{fmtRM2(d.amountDue)}</b> · {d.paymentMethod} · <StatusBadge status={d.paymentStatus} /></span>
              <span>Rider: {d.riderId ? riderRoster.find((r) => r.id === d.riderId)?.name : <b className="text-kelate-red">Unassigned</b>}</span>
              <span>{timeAgo(d.createdAt)}</span>
            </div>
            {!d.riderId && (
              <button data-testid={`assign-${d.deliveryNumber}`} onClick={() => setAssignTarget(d)} className="mt-3 rounded-sm bg-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
                Assign Delivery Staff
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!assignTarget} onOpenChange={() => setAssignTarget(null)}>
        <DialogContent className="bg-kelate-surface border-kelate-line max-w-sm" data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle className="text-kelate-ink">Assign {assignTarget?.deliveryNumber}</DialogTitle>
            <DialogDescription className="text-kelate-soft">Choose a KELATE delivery staff member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {riderRoster.map((r) => {
              const avail = riderAvailability[r.id] !== undefined ? riderAvailability[r.id] : r.riderAvailable !== false;
              return (
                <button key={r.id} data-testid={`assign-rider-${r.id}`} onClick={() => { assignRider(assignTarget.id, r.id, r.name); setAssignTarget(null); toast.success(`${r.name} assigned to ${assignTarget.deliveryNumber}`); }}
                  className="flex w-full items-center justify-between rounded-sm border border-kelate-line px-3 py-2.5 text-sm hover:border-kelate-wood">
                  <span className="font-medium text-kelate-ink">{r.name}</span>
                  <span className="flex items-center gap-1.5 text-xs text-kelate-soft" data-testid={`rider-availability-${r.id}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${avail ? 'bg-kelate-sage' : 'bg-kelate-soft/50'}`} />
                    {avail ? 'Available' : 'Offline'} · {r.zone}
                  </span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DeliverySettings() {
  const { deliverySettings, updateDeliverySettings } = useApp();
  const [form, setForm] = useState(deliverySettings);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const input = "h-10 w-full rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  return (
    <div className="max-w-lg" data-testid="admin-delivery-settings">
      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5">
        <h3 className="text-sm font-semibold text-kelate-ink">Delivery Reference Point & Fees</h3>
        <p className="mt-1 text-xs text-kelate-soft">Zone logic: within local radius of the reference point → local fee; elsewhere in Kelantan → Kelantan fee; outside Kelantan → outside fee. Changes only affect new orders (existing orders keep their fee snapshot).</p>
        <div className="mt-4 space-y-3">
          <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Reference Point Name</label><input data-testid="ds-name" value={form.referenceName} onChange={(e) => set('referenceName', e.target.value)} className={input} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Latitude</label><input data-testid="ds-lat" type="number" step="0.0001" value={form.lat} onChange={(e) => set('lat', Number(e.target.value))} className={input} /></div>
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Longitude</label><input data-testid="ds-lng" type="number" step="0.0001" value={form.lng} onChange={(e) => set('lng', Number(e.target.value))} className={input} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Local Radius (KM)</label><input data-testid="ds-radius" type="number" value={form.radiusKm} onChange={(e) => set('radiusKm', Number(e.target.value))} className={input} /></div>
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Local Fee (RM)</label><input data-testid="ds-local-fee" type="number" value={form.localFee} onChange={(e) => set('localFee', Number(e.target.value))} className={input} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Kelantan Fee (RM)</label><input data-testid="ds-kelantan-fee" type="number" value={form.kelantanFee} onChange={(e) => set('kelantanFee', Number(e.target.value))} className={input} /></div>
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Outside Kelantan Fee (RM)</label><input data-testid="ds-outside-fee" type="number" value={form.outsideFee} onChange={(e) => set('outsideFee', Number(e.target.value))} className={input} /></div>
          </div>
          <button data-testid="ds-save" onClick={() => updateDeliverySettings(form)} className="rounded-sm bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Save Settings</button>
        </div>
      </div>
    </div>
  );
}

